/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Back, Timeline, TimelineLite, TweenLite} from "gsap";
import {FruitsLayerConfig} from "../../../config/FruitsLayerConfig";
import {FruitsParticles} from "../../animationsbits/fruitsparticles/FruitsParticles";
import {Fruits} from "../../Fruits";
import {FruitsGameAssets} from "../../FruitsGameAssets";
import {FruitsGameConfig} from "../../FruitsGameConfig";
import {FruitsGameModuleConfig} from "../../FruitsGameModuleConfig";
import {FruitsBigWinCountUp} from "./FruitsBigWinCountUp";

export class FruitsBigWinView extends BaseView {

    private _layer:ScalableLayer;
    private readonly _countUp:FruitsBigWinCountUp;
    private _isShowing:boolean = false;
    private _bgImageContainer:PIXI.Container;
    private _backgroundImage:PIXI.TilingSprite;
    private _centerX:number;
    private _centerY:number;
    private _splash:PIXI.Sprite;
    private _middleContainer:PIXI.Container;
    private _stars:TimelineSprite;
    private _bgRays:PIXI.Sprite;
    private _bigWinTexts:PIXI.Sprite[];
    private _bigWinTextsContainer:PIXI.Container;
    private _highOrderLayer:ScalableLayer;
    public _particles:FruitsParticles;
    private _screenWidth:number;
    private _screenHeight:number;
    private _leftOffset:number;
    private _topOffset:number;

    constructor() {
        super();

        Fruits.bigWinView = this;

        this._layer = StageManager.getLayer(FruitsGameConfig.instance.LAYERS.WIN_PRESENTATIONS.name);
        this._countUp = new FruitsBigWinCountUp();
        this._highOrderLayer = StageManager.getLayer(FruitsLayerConfig.HIGH_ORDER_LAYER.name)
    }

    protected initAnimations():void {
        this._particles = FruitsGameModuleConfig.particles;

        this._bgImageContainer = new PIXI.Container();
        this._bgImageContainer.alpha = 0;


        this._middleContainer = new PIXI.Container();

        this._countUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);

        this._bigWinTexts = [];
        this._bigWinTextsContainer = new PIXI.Container();

        this._layer.addChild(this._bgImageContainer);
        this._layer.addChild(this._middleContainer);
        this._highOrderLayer.addChild(this._countUp);

        this.createBigWinTexts();
        this.createFirstLevel();
        this.createSecondLevel();
        this.createThirdLevel();


        this.createBg();
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        this._isResizeDirty = false;
        this._screenWidth = Math.ceil(resizeData.width / this._layer.scale.x);
        this._screenHeight = Math.ceil(resizeData.height / this._layer.scale.y);
        this._leftOffset = Math.floor(-this._layer.x / this._layer.scale.x);
        this._topOffset = Math.floor(-this._layer.y / this._layer.scale.y);

        if(resizeData.orientation === Orientation.PORTRAIT) {
            this._bgRays.rotation = 90 * PIXI.DEG_TO_RAD;
            this._bgRays.scale.set(1.5, 1.5);
        } else {
            this._bgRays.rotation = 0;
            this._bgRays.scale.set(1.5, 1.5);
        }

        this._centerX = this._screenWidth / 2 + this._leftOffset;
        this._centerY = this._screenHeight / 2 + this._topOffset;


        // centered in the window.
        this._countUp.position.set(this._centerX, this._centerY - 50);
        this._splash.position.set(this._centerX, this._centerY - 50);
        this._stars.position.set(this._centerX, this._centerY - 50);
        this._bgRays.position.set(this._centerX, this._centerY - 50);

        this.bigWinTextsResize();
        this.bgResize();
    }

    public getBigWinLevelAnimation(level:number, winRatioConfig:IWinRatioConfig):Timeline {
        this._isShowing = true;
        if(this._isResizeDirty) {
            this.onResize(this._resizeData);
        }

        const timeline:TimelineLite = new TimelineLite();

        timeline.add([
            this.getLevelsAnimation(level),
            this.bigWinTextAnimation(level),
            this.bgAnimation(level),
            () => this._particles.startFruitEmitter(level)
        ]);
        let totDur = timeline.totalDuration();
        timeline.set({}, {}, `+=${winRatioConfig.duration - totDur}`);
        return timeline;
    }


    public playEndingAnimation(winNumber:number, endWinRatioConfig:IWinRatioConfig, winLevel:number, isAborted:boolean):TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        tl.add([
            () => this._particles.getResetEmitters(),
            () => this.resetBigWinText(),
        ]);

        tl.add(this._countUp.getEndingAnimation(winNumber, winLevel, isAborted));

        tl.add(() => this._isShowing = false);


        return tl;
    }

    public playCountUp(from:number, to:number, duration:number, level:number):Timeline | null {
        return this._countUp.getCountUpAnimation(from, to, duration, level);
    }

    public bgAnimation(level:number, initial:boolean = false):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        this.bgResize(level);
        if(initial) {
            tl.add(TweenLite.to(this._bgImageContainer, 1.6, {alpha : 1}));
        } else if(level > 0) {
            tl.add(TweenLite.to(this._bgImageContainer, 0.2, {alpha : 1}));
        }
        return tl;
    }

    private createBg():void {
        this._backgroundImage = new PIXI.TilingSprite(
            GameResources.getTextures(FruitsGameAssets.getBGTexture("1"))[0],
            GameConfig.instance.GAME_WIDTH,
            GameConfig.instance.GAME_HEIGHT
        );
    }

    private bgResize(level:number = 0):void {
        this._bgImageContainer.removeChild(this._backgroundImage);

        if(level > 2) {
            level = 2
        }

        let spriteTexture = FruitsGameAssets.getBGTexture(`${level}`);


        this._backgroundImage = new PIXI.TilingSprite(
            GameResources.getTextures(spriteTexture)[0],
            this._screenWidth,
            this._screenHeight
        );


        this._backgroundImage.tileScale.y = this._screenHeight / 720;
        this._backgroundImage.anchor.set(0.5, 0.5);
        this._backgroundImage.position.set(this._centerX, this._centerY);

        this._bgImageContainer.addChild(this._backgroundImage);
    }

    private getLevelsAnimation(level:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(level === 0) {
            return this.firstLevelAnimation();
        } else if(level === 1) {
            tl.add([
                this.firstLevelAnimation(),
                this.secondLevelAnimation()
            ]);

            return tl;
        } else if(level === 2) {
            tl.add([
                this.secondLevelAnimation(),
                this.thirdLevelAnimation()
            ]);

            return tl;
        } else {
            return this.firstLevelAnimation();
        }


    }


    /*============== START - BIG WIN TEXT ==============*/

    private bigWinTextAnimation(level:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(level === 0) {
            tl.add([
                TweenLite.fromTo(this._bigWinTexts[level], 0.1, {alpha : 0}, {alpha : 1}),
                TweenLite.fromTo(this._bigWinTextsContainer.scale, 0.2, {x : 1.5, y : 1.5}, {
                    x : 1,
                    y : 1,
                    ease : Back.easeOut.config(2)
                })
            ])
        } else {

            tl.add([
                TweenLite.fromTo(this._bigWinTexts[level], 0.1, {alpha : 0}, {alpha : 1}),
                TweenLite.fromTo(this._bigWinTextsContainer.scale, 0.2, {x : 1.5, y : 1.5}, {
                    x : 1,
                    y : 1,
                    ease : Back.easeOut.config(2)
                }),
                () => this._bigWinTexts[level - 1].alpha = 0
            ])
        }


        return tl;
    }

    private createBigWinTexts():void {
        this._highOrderLayer.addChild(this._bigWinTextsContainer);
        for(let i:number = 0; i < 3; i++) {
            let sprite = new PIXI.Sprite(GameResources.getTextures(`big_win/Text_${i}`)[0]);
            sprite.anchor.set(0.5, 0.5);
            sprite.alpha = 0;
            this._bigWinTextsContainer.addChild(sprite);
            this._bigWinTexts.push(sprite);
        }
    }

    private bigWinTextsResize():void {
        const maxWidth:number = this._screenWidth * 0.9;
        this._bigWinTexts.forEach(text => {
            text.scale.set(1);
            if(text.width > maxWidth) {
                let scale = maxWidth / text.width;
                text.scale.set(scale);
            }
        });
        this._bigWinTextsContainer.position.set(this._centerX, this._centerY - 200);
    }

    private resetBigWinText():void {
        this._bigWinTexts.forEach(text => {
            TweenLite.to(text, 0.5, {alpha : 0});
        })
    }

    /*=============== END - BIG WIN TEXT ===============*/

    /*============== START - LEVEL 1 ==============*/


    public firstLevelAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        tl.add([
            () => this._splash.alpha = 1,
            TweenLite.to(this._splash.scale, 0.15, {x : 2, y : 2})
        ]);

        tl.add(TweenLite.to(this._splash, 0.8, {alpha : 0}));

        return tl;

    }


    private createFirstLevel():void {
        this._splash = new PIXI.Sprite(GameResources.getTextures("winFXSplash")[0]);
        this._splash.blendMode = PIXI.BLEND_MODES.ADD;
        this._splash.scale.set(0.4, 0.4);
        this._splash.alpha = 0;
        this._splash.anchor.set(0.5, 0.5);
        this._middleContainer.addChild(this._splash);

    }

    /*=============== END - LEVEL 1 ===============*/


    /*============== START - LEVEL 2 ==============*/

    private createSecondLevel():void {
        this._stars = new TimelineSprite(GameResources.getTextures("stars"));
        this._stars.hide();
        this._stars.blendMode = PIXI.BLEND_MODES.ADD;
        this._stars.anchor.set(0.5, 0.5);
        this._middleContainer.addChild(this._stars);

    }

    public secondLevelAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            () => this._stars.alpha = 1,
            this._stars.getAnimationAutoShowHide(true, false),
            new TweenLite(this._stars.scale, 1, {x : 3, y : 3})
        ]);
        tl.add(TweenLite.to(this._stars, 0.2, {alpha : 0}));
        tl.add(() => this._stars.hide());


        return tl;
    }

    /*=============== END - LEVEL 2 ===============*/


    /*============== START - LEVEL 3 ==============*/

    private createThirdLevel():void {
        this._bgRays = new PIXI.Sprite(GameResources.getTextures("Bg_Rays")[0]);
        this._bgRays.blendMode = PIXI.BLEND_MODES.ADD;
        this._bgRays.scale.set(1.5, 1.5);
        this._bgRays.alpha = 0;
        this._bgRays.anchor.set(0.5, 0.5);
        this._middleContainer.addChild(this._bgRays);
    }

    public thirdLevelAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        tl.add([
            () => this._bgRays.alpha = 1,
            TweenLite.to(this._bgRays, 1, {alpha : 0})
        ]);

        return tl;
    }

    /*=============== END - LEVEL 3 ===============*/

    /*============== START - END ANIMATION ==============*/

    public fadeOutBg():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        tl.add(TweenLite.to(this._bgImageContainer, 0.25, {alpha : 0}));

        return tl;
    }

    /*=============== END - END ANIMATION ===============*/

    public resetBigWinView():void {
        this._stars.scale.set(1, 1);
        this._stars.alpha = 0;
        this._stars.hide();

        this._splash.scale.set(0.4, 0.4);
        this._splash.alpha = 0;

        this._bgRays.scale.set(1.5, 1.5);
        this._bgRays.alpha = 0;
        this._bgRays.anchor.set(0.5, 0.5);
    }

    public fadeOutBigWin():void {
        this._countUp.fadeOutBigWinCountUp();
    }
}